import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

class CartNotification extends HTMLElement {
  get #popup() {
    return this.querySelector('[data-ref="popup"]');
  }

  get #productPanel() {
    return this.querySelector('[data-ref="productPanel"]');
  }

  get #product() {
    return this.querySelector('[data-ref="product"]');
  }

  get #cartError() {
    return this.querySelector('[data-ref="cartError"]');
  }

  get #cartErrorText() {
    return this.#cartError.querySelector('span');
  }

  get #closeElements() {
    return this.querySelectorAll('[data-ref="close"]');
  }

  connectedCallback() {
    document.addEventListener('submit', this.#onProductFormSubmit);

    document.addEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#onCartError);

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);

    this.#closeElements.forEach((element) =>
      element.addEventListener('click', this.#onCloseClick)
    );
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.#onProductFormSubmit);

    document.removeEventListener(ThemeEvents.cartAdd, this.#onCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#onCartError);

    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);

    this.#closeElements.forEach((element) =>
      element.removeEventListener('click', this.#onCloseClick)
    );
  }

  #onProductFormSubmit = () => {
    this.#showSelf();
    this.#showLoading();
  };

  #onCartAdd = ({ detail }) => {
    const { lineItem, data: { sections } } = detail;
    const html = sections['cart-notification-product'];

    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newContent = parsed.getElementById(`cart-notification-product-${lineItem.key}`);
      if (newContent) {
        this.#product.replaceChildren(...newContent.children);
      }
    }

    this.#hideCartError();
    this.#showProductPanel();
    this.#hideLoading();
    this.#showPopup();
  };

  #onCartError = ({ detail }) => {
    const { message } = detail.data;

    this.#hideProductPanel();
    this.#showCartError(message);
    this.#hideLoading();
    this.#showPopup();
  };

  #showSelf() {
    this.removeAttribute('data-closing');
    this.setAttribute('data-open', '');
  }

  #hideSelf() {
    this.removeAttribute('data-open');
    this.removeAttribute('data-loading');
  }

  #showLoading() {
    this.setAttribute('data-loading', '');
  }

  #hideLoading() {
    this.removeAttribute('data-loading');
  }

  #showPopup() {
    this.#popup.removeAttribute('data-closing');
    this.#popup.setAttribute('data-open', '');
  }

  #hidePopup() {
    this.#popup.setAttribute('data-closing', '');
    this.#popup.removeAttribute('data-open');

    this.#popup.addEventListener(
      'transitionend',
      () => this.#popup.removeAttribute('data-closing'),
      { once: true }
    );
  }

  #showProductPanel() {
    this.#productPanel.removeAttribute('hidden');
  }

  #hideProductPanel() {
    this.#productPanel.setAttribute('hidden', '');
  }

  #showCartError(message) {
    this.#cartErrorText.textContent = message;
    this.#cartError.removeAttribute('hidden');
  }

  #hideCartError() {
    this.#cartError.setAttribute('hidden', '');
  }

  hide() {
    this.#hideSelf();
    this.#hidePopup();
  }

  #onCloseClick = () => this.hide();

  #onDocumentClick = ({ target }) => {
    if (target instanceof Node && !this.contains(target)) {
      this.hide();
    }
  };

  #onDocumentKeydown = ({ key }) => {
    if (key === 'Escape') this.hide();
  };
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}

function ProductFormsController() {
  /**
   * Intercepts submissions from any /cart/add form that is NOT already
   * managed by a product-form custom element, either as an ancestor or
   * a descendant of the form.
   *
   * Handles the fetch and dispatches
   * CartAddEvent or CartErrorEvent so cart-notification and other
   * listeners can respond without knowing the source.
   */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action?.includes('/cart/add')) {
      return;
    }

    if (form.closest('product-form') || form.querySelector('product-form')) {
      return;
    }

    event.preventDefault();

    const formData = new FormData(form);
    formData.set('sections', 'cart-notification-product');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        form.dispatchEvent(new CartErrorEvent(
          form.id,
          data.message,
          data.description,
          data.errors,
        ));
      } else {
        form.dispatchEvent(new CartAddEvent(data, form.id, {
          sections: data.sections,
        }));
      }
    } catch (error) {
      console.error('Add to cart failed', error);
    }
  });
}

ProductFormsController();
